import {
  DefaultVariant,
  DefaultVariantRow,
  DiscountTierDto,
  OfferProductDto,
  ProductOptionFe,
  VariantMixDropdownType,
  VariantSettings,
  VariantSettingsProduct,
  VariantSettingsUpdate,
} from "@intelligems/ig-types";

export function getSelectedOptionData(
  productOptions: ProductOptionFe[],
  selectedOption: string
): ProductOptionFe | undefined {
  return productOptions.find((opt) => opt.name === selectedOption);
}

export function getDefaultVariantRows(
  productOptions: ProductOptionFe[],
  maxUnits: number
): DefaultVariantRow[] {
  const rowCount = maxUnits;
  return Array.from({ length: rowCount }, (_, index) => ({
    index,
    options: productOptions,
  }));
}

export const mergeVariantSettings = (
  currentSettings: VariantSettings | undefined,
  updates: VariantSettingsUpdate,
  productId: string,
  productOptions: ProductOptionFe[],
  maxUnits: number,
  productImage?: string
): VariantSettings => {
  const optionDefaults = getDefaultOptionTypes(productOptions);
  const optionValues = getAllOptionValues(productOptions);

  const products = currentSettings?.products
    ? currentSettings.products.map((product) => ({ ...product }))
    : [];

  const productUpdate = updates.product;
  if (productUpdate) {
    const targetProductId = productUpdate.productId ?? productId;
    const existingIndex = products.findIndex(
      (product) => product.productId === targetProductId
    );
    const existingProduct =
      existingIndex >= 0 ? { ...products[existingIndex] } : undefined;

    const mergedOptionTypes = {
      ...optionDefaults,
      ...(existingProduct?.optionTypes ?? {}),
      ...(productUpdate.optionTypes ?? {}),
    };

    const mergedVariantColors =
      productUpdate.variantColors !== undefined
        ? productUpdate.variantColors
        : (existingProduct?.variantColors ?? {});

    const mergedVariantImages =
      productUpdate.variantImages !== undefined
        ? productUpdate.variantImages
        : (existingProduct?.variantImages ?? {});

    const mergedDefaultVariants =
      productUpdate.defaultVariants !== undefined
        ? productUpdate.defaultVariants
        : (existingProduct?.defaultVariants ??
          generateDefaultVariants(productOptions, maxUnits));

    const mergedAllOptionValues =
      productUpdate.allOptionValues !== undefined
        ? productUpdate.allOptionValues
        : (existingProduct?.allOptionValues ?? optionValues);

    const mergedProduct: VariantSettingsProduct = {
      productId: targetProductId,
      productImage:
        productUpdate.productImage !== undefined
          ? productUpdate.productImage
          : (productImage ?? existingProduct?.productImage),
      optionTypes: mergedOptionTypes,
      allOptionValues: mergedAllOptionValues,
      defaultVariants: mergedDefaultVariants,
      variantColors: mergedVariantColors,
      variantImages: mergedVariantImages,
    };

    if (existingIndex >= 0) {
      products[existingIndex] = mergedProduct;
    } else {
      products.push(mergedProduct);
    }
  }

  return {
    enabled: updates.enabled ?? currentSettings?.enabled ?? false,
    products,
  };
};

/**
 * Values stored for a single option, or `[]` when the saved settings have no
 * entry for it. `allOptionValues` is a snapshot taken when the settings were
 * last written, so an option the live product has gained (or had renamed)
 * since then is simply absent — the settings are stale, not corrupt.
 */
export const getStoredOptionValues = (
  productSettings: VariantSettingsProduct | undefined,
  optionName: string
): string[] => productSettings?.allOptionValues?.[optionName] ?? [];

export const getDefaultVariantValue = (
  productSettings: VariantSettingsProduct | undefined,
  rowIndex: number,
  optionName: string,
  // Callers pass the option value at the row's index, and a row index can run
  // past the end of that list — the row count comes from the button count, not
  // from how many values the option has.
  fallbackValue: string | undefined
): string => {
  return (
    productSettings?.defaultVariants?.[rowIndex]?.[optionName] ??
    fallbackValue ??
    ""
  );
};

export const updateDefaultVariantAtIndex = (
  currentVariants: DefaultVariant[] | undefined,
  rowIndex: number,
  optionName: string,
  value: string
): DefaultVariant[] => {
  const variants: DefaultVariant[] = [...(currentVariants || [])];

  variants[rowIndex] = {
    ...variants[rowIndex],
    [optionName]: value,
  };

  return variants;
};

export const updateOptionMapping = (
  optionName: string,
  optionValue: string,
  newValue: string,
  current: Record<string, Record<string, string>>
): Record<string, Record<string, string>> => {
  const next = { ...current };
  const optionEntries = { ...(next[optionName] ?? {}) };

  if (newValue) {
    optionEntries[optionValue] = newValue;
    next[optionName] = optionEntries;
    return next;
  }

  delete optionEntries[optionValue];
  if (Object.keys(optionEntries).length === 0) {
    delete next[optionName];
    return next;
  }

  next[optionName] = optionEntries;
  return next;
};

function getDefaultOptionTypes(
  productOptions: ProductOptionFe[]
): Record<string, VariantMixDropdownType> {
  return productOptions.reduce(
    (acc, option) => {
      acc[option.name] = "default";
      return acc;
    },
    {} as Record<string, VariantMixDropdownType>
  );
}

function getAllOptionValues(
  productOptions: ProductOptionFe[]
): Record<string, string[]> {
  return productOptions.reduce(
    (acc, option) => {
      acc[option.name] = option.values || [];
      return acc;
    },
    {} as Record<string, string[]>
  );
}

// always use the first value for all rows
function pickSafeValue(values: string[]): string {
  if (!values || values.length === 0) return "";
  return values[0];
}

export function generateDefaultVariants(
  productOptions: ProductOptionFe[],
  maxUnits: number
): DefaultVariant[] {
  const variants: DefaultVariant[] = [];
  for (let rowIndex = 0; rowIndex < maxUnits; rowIndex++) {
    const variant: DefaultVariant = {};
    for (const option of productOptions) {
      variant[option.name] = pickSafeValue(option.values || []);
    }
    variants.push(variant);
  }
  return variants;
}

export function checkAllVariantsSamePrice(
  offerProducts: OfferProductDto[]
): boolean {
  if (!offerProducts.length) return true;
  const includedVariants = offerProducts.flatMap((product) =>
    product.variants.filter((v) => v.inclusionType === "include")
  );
  if (includedVariants.length === 0) return true;
  const firstPrice = includedVariants[0].price;
  return includedVariants.every((v) => v.price === firstPrice);
}

export const tierOneHasDiscount = (tiers: DiscountTierDto[]): boolean => {
  const tierOne = tiers.find((t) => t.minimumUnits === 1);
  return Boolean(
    tierOne &&
    (tierOne.unitDiscount > 0 ||
      tierOne.isFreeShipping ||
      tierOne.isGiftWithPurchase)
  );
};
